/**
 * Small, additive "data-grade" UI primitives (CSS visual-language plan,
 * Phase 2). Thin wrappers over the `.label-micro` / `.stat-tile` /
 * `.row-list` classes added to `index.css` \u2014 nothing here replaces or
 * renames an existing component; these are new, opt-in building blocks
 * for surfaces that need the denser, monospace-numeral, hairline-bordered
 * register (paid-tier paywalls, ROI/KPI callouts, demo previews) rather
 * than the general-purpose `.card` treatment.
 */
import type { ReactNode } from "react";

/** A small uppercase, letter-spaced, monospace eyebrow label. */
export function MicroLabel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`label-micro ${className}`}>{children}</div>;
}

/** A pulsing status dot (e.g. "live"/"active" indicators). */
export function LiveDot({ className = "" }: { className?: string }) {
  return <span className={`live-dot ${className}`} aria-hidden="true" />;
}

/** A dense KPI/ROI callout: mono tabular value + micro label, hairline border, thin accent rule. */
export function StatTile({ value, label, className = "" }: { value: ReactNode; label: ReactNode; className?: string }) {
  return (
    <div className={`stat-tile ${className}`}>
      <div className="stat-tile-value">{value}</div>
      <div className="stat-tile-label">{label}</div>
    </div>
  );
}

/** A dense checklist \u2014 hairline-divided rows instead of loosely-spaced `space-y-*` lists. */
export function RowList({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`row-list ${className}`}>{children}</div>;
}

export function RowListItem({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`row-list-item ${className}`}>{children}</div>;
}
